import {
  FilePenLine,
  LockKeyhole,
  Plus,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from "lucide-react";
import type { AgentWorkspaceEditPolicy } from "@lumen/shared";
import { useState } from "react";
import { t } from "../../i18n";

type PolicyAction = AgentWorkspaceEditPolicy[string];

function policyWithFallback(
  value: AgentWorkspaceEditPolicy,
  fallback: PolicyAction,
) {
  return Object.fromEntries([
    ["*", fallback],
    ...Object.entries(value).filter(([pattern]) => pattern !== "*"),
  ]) as AgentWorkspaceEditPolicy;
}

function policyWithRule(
  value: AgentWorkspaceEditPolicy,
  index: number,
  pattern: string,
  action: PolicyAction,
) {
  const next = Object.entries(value).filter(
    ([currentPattern]) => currentPattern !== "*",
  );
  next[index] = [pattern, action];
  return policyWithFallback(Object.fromEntries(next), value["*"] ?? "deny");
}

function policyWithoutRule(value: AgentWorkspaceEditPolicy, index: number) {
  const next = Object.entries(value).filter(([pattern]) => pattern !== "*");
  next.splice(index, 1);
  return policyWithFallback(Object.fromEntries(next), value["*"] ?? "deny");
}

export function AgentWorkspaceEditPolicyEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: AgentWorkspaceEditPolicy;
  onChange: (value: AgentWorkspaceEditPolicy) => void;
  disabled?: boolean;
}) {
  const [addingRule, setAddingRule] = useState(false);
  const [newPattern, setNewPattern] = useState("");
  const [newAction, setNewAction] = useState<PolicyAction>("allow");
  const [addError, setAddError] = useState("");
  const fallback = value["*"] ?? "deny";
  const rules = Object.entries(value).filter(([pattern]) => pattern !== "*");
  const allowsAnythingByDefault = fallback === "allow";
  const hasAllowRule = rules.some(([, action]) => action === "allow");
  const hasDenyRule = rules.some(([, action]) => action === "deny");
  const summary =
    allowsAnythingByDefault && !hasDenyRule
      ? t("Can edit all workspace files")
      : !allowsAnythingByDefault && !hasAllowRule
        ? t("Read-only: cannot change workspace files")
        : t("Custom path rules");

  const addRule = () => {
    const pattern = newPattern.trim();
    if (!pattern) {
      setAddError(t("Enter a file pattern first."));
      return;
    }
    if (pattern === "*") {
      setAddError(t("Use the default access control above for the * rule."));
      return;
    }
    if (rules.some(([currentPattern]) => currentPattern === pattern)) {
      setAddError(t("That file pattern is already listed."));
      return;
    }
    onChange({
      ...policyWithFallback(value, fallback),
      [pattern]: newAction,
    });
    setNewPattern("");
    setNewAction("allow");
    setAddError("");
    setAddingRule(false);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 dark:border-white/10">
      <div className="flex items-start gap-3 bg-stone-50 p-4 dark:bg-white/5">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
          <FilePenLine size={18} />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <strong className="text-sm">{t("Workspace edit access")}</strong>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-stone-500 dark:bg-white/10 dark:text-stone-300">
              {summary}
            </span>
          </div>
          <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-stone-400">
            {t(
              "Control which files this agent may change. A path rule overrides the default when it matches.",
            )}
          </p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-stone-700 dark:text-stone-200">
            <LockKeyhole size={14} /> {t("Default access")}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(policyWithFallback(value, "deny"))}
              className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${fallback === "deny" ? "border-lumen-400 bg-lumen-50 ring-1 ring-lumen-400 dark:border-lumen-600 dark:bg-lumen-900/20" : "border-stone-200 hover:border-stone-300 dark:border-white/10 dark:hover:border-white/20"}`}
            >
              <ShieldOff className="mt-0.5 shrink-0 text-stone-500" size={17} />
              <span>
                <strong className="block text-sm">{t("Read-only")}</strong>
                <span className="block text-xs text-stone-500 dark:text-stone-400">
                  {t(
                    "The agent cannot make file changes unless a rule allows them.",
                  )}
                </span>
              </span>
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(policyWithFallback(value, "allow"))}
              className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${fallback === "allow" ? "border-lumen-400 bg-lumen-50 ring-1 ring-lumen-400 dark:border-lumen-600 dark:bg-lumen-900/20" : "border-stone-200 hover:border-stone-300 dark:border-white/10 dark:hover:border-white/20"}`}
            >
              <ShieldCheck
                className="mt-0.5 shrink-0 text-lumen-600"
                size={17}
              />
              <span>
                <strong className="block text-sm">{t("Can edit files")}</strong>
                <span className="block text-xs text-stone-500 dark:text-stone-400">
                  {t(
                    "The agent may edit files by default, with exceptions below.",
                  )}
                </span>
              </span>
            </button>
          </div>
        </div>

        <div className="border-t border-stone-100 pt-4 dark:border-white/10">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-xs font-semibold text-stone-700 dark:text-stone-200">
                {t("Path-specific rules")}
              </div>
              <p className="mt-1 text-[11px] leading-5 text-stone-400">
                {t(
                  "Use * for any characters and ? for one character. The most specific match wins.",
                )}
              </p>
            </div>
            {!addingRule && !disabled && (
              <button
                type="button"
                className="button shrink-0 text-xs"
                onClick={() => {
                  setAddingRule(true);
                  setAddError("");
                }}
              >
                <Plus size={14} /> {t("Add rule")}
              </button>
            )}
          </div>

          {rules.length > 0 && (
            <div className="space-y-2">
              {rules.map(([pattern, action], index) => (
                <div
                  key={`${index}-${pattern}`}
                  className="grid gap-2 rounded-xl border border-stone-200 p-2 sm:grid-cols-[minmax(0,1fr)_120px_auto] sm:items-center dark:border-white/10"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={`size-2 shrink-0 rounded-full ${action === "allow" ? "bg-emerald-500" : "bg-rose-500"}`}
                      aria-hidden="true"
                    />
                    <input
                      className="field min-w-0 py-2 font-mono text-xs"
                      disabled={disabled}
                      aria-label={t("File pattern")}
                      value={pattern}
                      onChange={(event) =>
                        onChange(
                          policyWithRule(
                            value,
                            index,
                            event.target.value,
                            action,
                          ),
                        )
                      }
                    />
                  </div>
                  <select
                    className="field py-2 text-xs font-semibold"
                    disabled={disabled}
                    aria-label={t("Rule action")}
                    value={action}
                    onChange={(event) =>
                      onChange(
                        policyWithRule(
                          value,
                          index,
                          pattern,
                          event.target.value as PolicyAction,
                        ),
                      )
                    }
                  >
                    <option value="allow">{t("Allow edits")}</option>
                    <option value="deny">{t("Block edits")}</option>
                  </select>
                  <button
                    type="button"
                    className="button p-2 text-stone-400 hover:text-red-600"
                    disabled={disabled}
                    aria-label={t("Remove rule")}
                    title={t("Remove rule")}
                    onClick={() => onChange(policyWithoutRule(value, index))}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {addingRule && (
            <div className="mt-2 rounded-xl border border-dashed border-lumen-300 bg-lumen-50/60 p-3 dark:border-lumen-700 dark:bg-lumen-900/10">
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_120px_auto] sm:items-start">
                <input
                  autoFocus
                  className="field py-2 font-mono text-xs"
                  placeholder={t("e.g. .env* or src/**")}
                  value={newPattern}
                  aria-label={t("New file pattern")}
                  onChange={(event) => {
                    setNewPattern(event.target.value);
                    setAddError("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addRule();
                    if (event.key === "Escape") setAddingRule(false);
                  }}
                />
                <select
                  className="field py-2 text-xs font-semibold"
                  value={newAction}
                  aria-label={t("New rule action")}
                  onChange={(event) =>
                    setNewAction(event.target.value as PolicyAction)
                  }
                >
                  <option value="allow">{t("Allow edits")}</option>
                  <option value="deny">{t("Block edits")}</option>
                </select>
                <div className="flex gap-2 sm:justify-end">
                  <button
                    type="button"
                    className="button text-xs"
                    onClick={() => setAddingRule(false)}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="button-primary text-xs"
                    onClick={addRule}
                  >
                    <Plus size={14} /> {t("Add")}
                  </button>
                </div>
              </div>
              {addError && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-300">
                  {addError}
                </p>
              )}
            </div>
          )}

          {rules.length === 0 && !addingRule && (
            <div className="rounded-xl border border-dashed border-stone-200 px-3 py-4 text-center text-xs text-stone-400 dark:border-white/10">
              {t("No exceptions. The default access applies to every file.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
